import React, { useCallback, useState, useEffect, useRef } from 'react';
import useIsMobile from '../../../hooks/useIsMobile';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Button, Divider, Typography } from 'antd';
import { SoundFilled } from '@ant-design/icons';
import useAudioCache from '../../../hooks/useAudioCache';
import useTranslation from '../../../hooks/useTranslation';
import { randomInt, shuffled } from '../../../utils/random';

const { Text } = Typography;


/**
 * Подсветка нажатого варианта: зелёная — верный, красная — нет. Остальные кнопки
 * остаются белыми с серой рамкой, пока по ним не нажали.
 */
const answerBackground = (buttonState, index) => {
    if (buttonState.index !== index) {
        return 'white';
    }
    return buttonState.isCorrect ? '#d4edda' : '#f8d7da';
};

const answerBorderColor = (buttonState, index) => {
    if (buttonState.index !== index) {
        return '#ccc';
    }
    return buttonState.isCorrect ? '#28a745' : '#dc3545';
};

function WordTranslationChoiceCard({ word, locale, handleCorrectClick, handleIncorrectClick }) {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const { playAudio: playByRef } = useAudioCache(
        `${word.headword}|${word.partOfSpeech}|${locale.code}`, locale.code);
    const [translationOptions, setTranslationOptions] = useState([]);
    const [buttonState, setButtonState] = useState({ index: null, isCorrect: null });
    // Сколько раз нажали и когда показали слово: и то и другое нужно журналу, а
    // восстановить их потом неоткуда.
    const attemptsRef = useRef(0);
    const shownAtRef = useRef(Date.now());
    // Флаг, который запоминает, что была допущена ошибка
    const [hasError, setHasError] = useState(false);

    // Озвучка по ref из ответа через общий кэш; проигрывание при показе карточки
    // сохранено ниже, в эффекте на смену слова.
    const playAudio = useCallback(() => {
        if (word.audio?.access === 'ALLOWED') {
            playByRef(word.audio.ref);
        }
    }, [word, playByRef]);
    // По нажатию можно и NOT_READY: файла ещё нет, и нажатие закажет синтез после
    // подтверждения. Автоматическое проигрывание такое не делает — там нет жеста.
    const playAudioOnClick = useCallback(() => {
        if (word.audio?.access === 'ALLOWED' || word.audio?.access === 'NOT_READY') {
            playByRef(word.audio.ref, word.audio.access);
        }
    }, [word, playByRef]);
    const generateOptions = useCallback(() => {
        // Выбираем случайный вариант из главного списка определений
        const mainDefs = word.wordDefinition || [];
        const correctOption = mainDefs.length > 0 ? (() => {
            const randomIndex = randomInt(mainDefs.length);
            const def = mainDefs[randomIndex];
            return {
                translation: def.translation,
                definition: def.definition,
                isCorrect: true,
            };
        })() : null;

        // Для auxiliaryItems выбираем из каждого случайный вариант определения
        const auxOptions = word.auxiliaryItems?.map(item => {
            const defs = item.wordDefinition;
            if (defs && defs.length > 0) {
                const randomIndex = randomInt(defs.length);
                const def = defs[randomIndex];
                return {
                    translation: def.translation,
                    definition: def.definition,
                    isCorrect: false,
                };
            }
            return null;
        }).filter(option => option !== null) || [];

        // Берем случайно до 3-х вариантов из дополнительных
        const filteredAux = shuffled(auxOptions).slice(0, 3);

        // Объединяем корректный вариант с дополнительными
        const combined = correctOption ? [correctOption, ...filteredAux] : [...filteredAux];
        const finalOptions = shuffled(combined).map((option, index) => ({
            ...option,
            id: index,
        }));

        setTranslationOptions(finalOptions);
    }, [word]);

    useEffect(() => {
        if (!word) return;
        // При загрузке нового слова сбрасываем состояние ошибки
        setHasError(false);
        generateOptions();
        playAudio();
        // И счётчики свидетельства: компонент не пересоздаётся между словами, поэтому
        // без сброса второе слово отчиталось бы попытками и временем первого.
        attemptsRef.current = 0;
        shownAtRef.current = Date.now();
    }, [word, generateOptions, playAudio]);



    const handleTranslationClick = (option, index) => {
        // Сохраняем текущее состояние ошибки
        const wasError = hasError;
        const chosenAt = Date.now();
        attemptsRef.current += 1;
        setButtonState({ index, isCorrect: option.isCorrect });

        if (!option.isCorrect) {
            // Если выбран неверный ответ, запоминаем ошибку и показываем анимацию ошибки,
            // но не переходим дальше
            setHasError(true);
            setTimeout(() => {
                setButtonState({ index: null, isCorrect: null });
            }, 500);
        } else {
            // Если выбран правильный вариант, переходим дальше только если выбран правильный ответ.
            // Но если ранее была допущена ошибка, финальный результат остаётся неверным.
            setTimeout(() => {
                setButtonState({ index: null, isCorrect: null });
                // Время раздумья замеряем по моменту выбора, а не по моменту вызова:
                // между ними полсекунды анимации, и они попали бы в журнал как раздумье.
                const evidence = {
                    optionCount: translationOptions.length,
                    attempts: attemptsRef.current,
                    responseMs: Math.max(0, chosenAt - shownAtRef.current),
                };
                if (wasError) {
                    handleIncorrectClick(evidence);
                } else {
                    handleCorrectClick(evidence);
                }
            }, 500);
        }
    };

    return (
        <div
            style={{
                padding: isMobile ? '0' : '0px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 'calc(100vh - 80px)',
                overflow: 'hidden',
                width: isMobile ? '100%' : '33%',
                margin: isMobile ? '0' : 'auto',
            }}
        >
            <Card style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                <CardContent style={{ flexGrow: 1, padding: isMobile ? '0px' : '20px' }}>
                    <div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '0px' }}>
                            <Text strong style={{ fontSize: '2em', color: '#027b83', textAlign: 'center' }}>
                                {word.headword}
                            </Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <button
                                type="button"
                                aria-label={t('common.listen')}
                                style={{
                                    borderRadius: '25%',
                                    padding: '3px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    background: 'none',
                                    border: '1.5px solid #838383',
                                }}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    playAudioOnClick();
                                }}
                            >
                                <SoundFilled style={{ fontSize: '0.9em', color: '#838383' }} />
                            </button>
                            <Text style={{ fontSize: '1em', marginLeft: '8px' }}>/{word.phonetic}/</Text>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Text strong style={{ fontSize: '1.2em', marginTop: '8px', textAlign: 'center' }}>
                                {word.partOfSpeech}
                            </Text>
                        </div>
                    </div>

                    <Divider style={{ margin: isMobile ? '0px 0' : '10px 0' }} />

                    <div style={{ marginTop: '30px' }}>
                        {translationOptions.map((option, index) => (
                            <Button
                                key={option.id}
                                onClick={() => handleTranslationClick(option, index)}
                                style={{
                                    display: 'block',
                                    margin: '10px auto',
                                    width: '100%',
                                    fontSize: '1.2em',
                                    backgroundColor: answerBackground(buttonState, index),
                                    borderColor: answerBorderColor(buttonState, index),
                                    transition: 'background-color 0.3s ease',
                                    padding: '12px 16px',
                                    height: 'auto',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                }}
                            >
                                <div style={{ textAlign: 'center', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                    <Text strong style={{ display: 'block', textAlign: 'left' }}>
                                        {option.definition}
                                    </Text>
                                    <Text italic strong style={{ display: 'block', textAlign: 'center', marginLeft: '12px', color: '#ee6002' }}>
                                        {option.translation}
                                    </Text>
                                </div>
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default WordTranslationChoiceCard;
